import { useCallback, useMemo, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { ErrorModal } from "@dexteel/mesf-core";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { DeleteConfirmModal } from "@/controls/modals/DeleteConfirmModal";
import type { RollMaterial } from "../models/RollMaterial";
import { ROLL_MATERIAL_INITIAL_VALUES } from "../models/RollMaterial";
import { useRollMaterials } from "../hooks/useRollMaterials";
import {
	useDeleteRollMaterial,
	useUpsertRollMaterial,
} from "../hooks/useRollMaterialMutation";
import { RollMaterialsTable } from "./RollMaterialsTable";
import { RollMaterialDetailViewer } from "./RollMaterialDetailViewer";
import { RollMaterialsToolbar } from "./RollMaterialsToolbar";
import { SHOW_ONLY_ACTIVES_DEFAULT } from "@/controls/filters/ShowOnlyActivesCheckbox";
import { urlParam, useUrlState, useUrlText } from "@/shared/hooks/useUrlState";
import { useUrlSelection } from "@/shared/hooks/useUrlSelection";

const DEFAULT_LAYOUT = { left: 55, right: 45 };

const URL_SCHEMA = {
	search: urlParam.string(),
	onlyActives: urlParam.bool(SHOW_ONLY_ACTIVES_DEFAULT),
	rollMaterialId: urlParam.int(),
};

export const RollMaterialsManager = () => {
	// Filters and the selected record, all in the query string.
	const [
		{ search, onlyActives, rollMaterialId },
		setFilters,
		{ dropValues, resetValues, isDefault },
	] = useUrlState(URL_SCHEMA);
	// The box types locally and the URL follows once typing stops.
	const [searchText, setSearchText] = useUrlText(search ?? "", (value) =>
		setFilters({ search: value || null }),
	);
	const [error, setError] = useState("");
	const [notification, setNotification] = useState("");
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [materialToDelete, setMaterialToDelete] = useState<RollMaterial | null>(
		null,
	);

	const { defaultLayout, onLayoutChange } = useDefaultLayout({
		id: "roll-materials-manager-v1",
		panelIds: ["left", "right"],
		storage: localStorage,
	});

	const { rollMaterials, isLoading, error: queryError } = useRollMaterials();

	const filteredMaterials = useMemo(() => {
		const base = onlyActives
			? rollMaterials.filter((m) => m.Active)
			: rollMaterials;
		if (!searchText) return base;
		const s = searchText.toLowerCase();
		return base.filter((m) => m.Name?.toLowerCase().includes(s));
	}, [rollMaterials, searchText, onlyActives]);

	// The id travels and the record is resolved from the list. A record being added has
	// no id yet, so it stays out of the URL.
	const selection = useUrlSelection<RollMaterial>({
		id: rollMaterialId,
		setId: (value) => setFilters({ rollMaterialId: value }),
		dropId: () => dropValues("rollMaterialId"),
		rows: rollMaterials,
		ready: !isLoading,
		getId: (record) => record.Id,
	});
	const selectedMaterial = selection.current;

	const { upsertRollMaterial, isUpserting } = useUpsertRollMaterial({
		onSuccess: (data) => {
			const isNew = !selectedMaterial?.Id;
			setNotification(
				isNew
					? "Roll material created successfully"
					: "Roll material updated successfully",
			);
			if (data) {
				selection.adopt(data);
			}
		},
		onError: (err) => setError(err.message),
	});

	const { deleteRollMaterial, isDeleting } = useDeleteRollMaterial({
		onSuccess: () => {
			setNotification("Roll material deleted successfully");
			selection.clear();
			setDeleteModalOpen(false);
			setMaterialToDelete(null);
		},
		onError: (err) => setError(err.message),
	});

	const handleRowSelected = useCallback(
		(record: RollMaterial | null) => {
			if (record) selection.select(record);
			else selection.clear();
		},
		[selection],
	);

	const handleDelete = useCallback((material: RollMaterial) => {
		setMaterialToDelete(material);
		setDeleteModalOpen(true);
	}, []);

	const handleConfirmDelete = useCallback(() => {
		if (materialToDelete?.Id) {
			deleteRollMaterial(materialToDelete.Id);
		}
	}, [materialToDelete, deleteRollMaterial]);

	const handleCancelDelete = useCallback(() => {
		setDeleteModalOpen(false);
		setMaterialToDelete(null);
	}, []);

	const handleAdd = useCallback(() => {
		selection.startNew({ ...ROLL_MATERIAL_INITIAL_VALUES });
	}, []);

	const handleSave = useCallback(
		(material: RollMaterial) => {
			upsertRollMaterial(material);
		},
		[upsertRollMaterial],
	);

	const handleClose = useCallback(() => {
		selection.clear();
	}, []);

	if (queryError) {
		return <ErrorModal error={queryError.message} onHide={() => {}} />;
	}

	return (
		<Box
			sx={{
				width: "100%",
				height: "calc(100vh - 130px)",
				display: "flex",
				flexDirection: "column",
				gap: 1,
				p: 1,
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Roll Materials
			</Typography>

			<Paper elevation={3}>
				<RollMaterialsToolbar
					onReset={() => resetValues(["search", "onlyActives"])}
					canReset={!isDefault("search", "onlyActives")}
					search={searchText}
					onSearchChange={setSearchText}
					onlyActives={onlyActives}
					onOnlyActivesChange={(value) => setFilters({ onlyActives: value })}
					onAdd={handleAdd}
				/>
			</Paper>

			<Group
				id="roll-materials-manager-group"
				orientation="horizontal"
				defaultLayout={defaultLayout ?? DEFAULT_LAYOUT}
				onLayoutChange={onLayoutChange}
				style={{ flex: 1, width: "100%" }}
			>
				<Panel id="left" minSize={25} style={{ paddingBottom: 5 }}>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							height: "100%",
							pr: 1,
						}}
					>
						<Paper
							elevation={3}
							sx={{ flex: 1, width: "100%", overflow: "hidden" }}
						>
							{isLoading ? (
								<CenteredLazyLoading />
							) : (
								<RollMaterialsTable
									rollMaterials={filteredMaterials}
									selectedMaterial={
										selectedMaterial?.Id ? selectedMaterial : null
									}
									onRowSelected={handleRowSelected}
									onDelete={handleDelete}
								/>
							)}
						</Paper>
					</Box>
				</Panel>

				<Separator
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "col-resize",
					}}
				>
					<Box
						sx={{
							width: 6,
							height: 40,
							borderRadius: 3,
							backgroundColor: "divider",
							"&:hover": { backgroundColor: "primary.main" },
						}}
					/>
				</Separator>

				<Panel id="right" minSize={30} style={{ paddingBottom: 5 }}>
					<Box sx={{ pl: 1, height: "100%" }}>
						<Paper
							elevation={3}
							sx={{
								height: "100%",
								overflowY: "auto",
								overflowX: "hidden",
							}}
						>
							<RollMaterialDetailViewer
								material={selectedMaterial}
								existingMaterials={rollMaterials}
								isSaving={isUpserting || isDeleting}
								onSave={handleSave}
								onClose={handleClose}
							/>
						</Paper>
					</Box>
				</Panel>
			</Group>

			<DeleteConfirmModal
				open={deleteModalOpen}
				title="DELETE ROLL MATERIAL"
				itemName={materialToDelete?.Name ?? ""}
				isDeleting={isDeleting}
				onConfirm={handleConfirmDelete}
				onCancel={handleCancelDelete}
			/>

			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};
